const BenefitsTab = ({ benefits, darkMode }) => {
  const getBenefitTypeLabel = (type) => {
    const labels = {
      'health_insurance': { label: '🏥 Seguro Saúde', color: 'bg-red-500/20 text-red-400' },
      'meal_allowance': { label: '🍽️ Sub. Alimentação', color: 'bg-amber-500/20 text-amber-400' },
      'transport': { label: '🚌 Transporte', color: 'bg-blue-500/20 text-blue-400' },
      'child_allowance': { label: '👶 Abono Família', color: 'bg-purple-500/20 text-purple-400' }
    };
    return labels[type] || { label: type, color: 'bg-slate-500/20 text-slate-400' };
  };

  return (
    <div>
      <h3 className={`text-xl font-bold mb-6 ${darkMode ? 'text-white' : 'text-slate-800'}`}>Meus Benefícios</h3>
      
      {/* Benefits Summary Card */}
      <div className="mb-6 p-4 bg-gradient-to-r from-emerald-600/20 to-blue-600/20 rounded-lg border border-emerald-500/30">
        <div className="flex items-center justify-between">
          <div>
            <p className={darkMode ? 'text-slate-400 text-sm' : 'text-slate-500 text-sm'}>Valor Total Mensal</p>
            <p className="text-2xl font-bold text-emerald-400">
              {benefits.total_monthly_value?.toLocaleString('pt-PT', { style: 'currency', currency: 'EUR' }) || '0,00 €'}
            </p>
          </div>
          <div className="text-4xl">💰</div>
        </div>
      </div>
      
      {/* Active Benefits */}
      {benefits.benefits?.length === 0 ? (
        <p className={`text-center py-8 ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>Sem benefícios atribuídos</p>
      ) : (
        <div className="space-y-4">
          <h4 className={`text-lg font-semibold ${darkMode ? 'text-white' : 'text-slate-800'}`}>Benefícios Ativos</h4>
          <div className="grid md:grid-cols-2 gap-4">
            {(benefits.benefits || []).map((benefit, idx) => {
              const typeInfo = getBenefitTypeLabel(benefit.benefit_type);
              return (
                <div key={idx} className={`p-4 rounded-lg ${darkMode ? 'bg-slate-700/50' : 'bg-slate-100'}`}>
                  <div className="flex items-center justify-between mb-2">
                    <span className={`font-medium ${darkMode ? 'text-white' : 'text-slate-800'}`}>
                      {benefit.name || benefit.benefit_type}
                    </span>
                    <span className="text-emerald-400 font-bold">
                      {benefit.monthly_value?.toLocaleString('pt-PT', { style: 'currency', currency: 'EUR' })}
                    </span>
                  </div>
                  <p className={`text-sm ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>{benefit.description}</p>
                  {benefit.benefit_type && (
                    <span className={`inline-block mt-2 px-2 py-0.5 rounded text-xs ${typeInfo.color}`}>
                      {typeInfo.label}
                    </span>
                  )}
                  
                  {/* Show eligible dependents for child-related benefits */}
                  {benefit.eligible_dependents && benefit.eligible_dependents.length > 0 && (
                    <div className={`mt-3 p-2 rounded ${darkMode ? 'bg-slate-800/50' : 'bg-slate-200'}`}>
                      <p className={`text-xs mb-1 ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>Dependentes Elegíveis:</p>
                      {benefit.eligible_dependents.map((dep, dIdx) => (
                        <p key={dIdx} className={`text-xs ${darkMode ? 'text-white' : 'text-slate-800'}`}>
                          • {dep.name} ({dep.age} anos)
                        </p>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
      
      {/* Family Members with Benefit Eligibility */}
      {benefits.family_members && benefits.family_members.length > 0 && (
        <div className="mt-8">
          <h4 className={`text-lg font-semibold mb-4 ${darkMode ? 'text-white' : 'text-slate-800'}`}>Elegibilidade de Dependentes</h4>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className={`text-left border-b ${darkMode ? 'text-slate-400 border-slate-600' : 'text-slate-500 border-slate-300'}`}>
                  <th className="pb-2">Nome</th>
                  <th className="pb-2">Parentesco</th>
                  <th className="pb-2">Idade</th>
                  <th className="pb-2">Abono Família</th>
                  <th className="pb-2">Apoio Estudante</th>
                </tr>
              </thead>
              <tbody>
                {benefits.family_members.map((member, idx) => (
                  <tr key={idx} className={`border-b ${darkMode ? 'border-slate-700' : 'border-slate-200'}`}>
                    <td className={`py-3 ${darkMode ? 'text-white' : 'text-slate-800'}`}>{member.name}</td>
                    <td className={`py-3 capitalize ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>{member.relationship}</td>
                    <td className={`py-3 ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>{member.age !== null ? `${member.age} anos` : '-'}</td>
                    <td className="py-3">
                      {member.eligible_for_child_benefits ? (
                        <span className="px-2 py-0.5 rounded text-xs bg-emerald-500/20 text-emerald-400">✅ Elegível</span>
                      ) : (
                        <span className="px-2 py-0.5 rounded text-xs bg-red-500/20 text-red-400">❌ Não elegível</span>
                      )}
                    </td>
                    <td className="py-3">
                      {member.eligible_for_student_benefits ? (
                        <span className="px-2 py-0.5 rounded text-xs bg-emerald-500/20 text-emerald-400">✅ Elegível</span>
                      ) : (
                        <span className="px-2 py-0.5 rounded text-xs bg-red-500/20 text-red-400">❌ Não elegível</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default BenefitsTab;
